import {drop, isNumber, values} from 'lodash';
import {useEffect, useRef, useState, useMemo, useContext, useCallback} from 'react';
import {IoContext, useSocketEvent} from 'socket.io-react-hook';
import {SocketLikeWithNamespace} from 'socket.io-react-hook/lib/cjs/types';

import {SocketEvent, SocketManagerEvent} from 'enums';
import {authDataStore} from 'services';
import {AuthDataStore} from 'types';

type TimeMessage<T> = {t: number, data: T};
type Message<T> = T | TimeMessage<T>;

const formatMessage = <T>(message: Message<T>): T => (message as TimeMessage<T>)?.t ? (message as TimeMessage<T>).data as T : message as T;

const extendCallback = <T>(
    callback: (message: T) => void, subscribedAt: number, opts?: {new: boolean}
) => (message: Message<T>) => {
        const timeMessage = message as TimeMessage<T>;
        const messageSentAt = timeMessage?.t;
        if (opts?.new && isNumber(messageSentAt)) {
            if (messageSentAt > subscribedAt) {
                callback(formatMessage(timeMessage));
            }
        } else {
            callback(timeMessage as any as T);
        }
    };

export const useSocketEventCallback = <T>(
    socket: SocketLikeWithNamespace,
    event: SocketEvent | SocketManagerEvent | string,
    callback?: (message: T) => void,
    opts?: {emitOnInit?: boolean, new?: boolean}
) => {
    const {setLastMessage} = useContext(IoContext);
    const {lastMessage} = useSocketEvent<T>(socket, event as any as string);
    const [lastMessageQueue, setLastMessageQueue] = useState([]);
    const initialized = useRef<boolean>();
    const resetOnLogout = useRef<boolean>();
    const subscribedOnLibSystemEvent = useRef<boolean>();
    const subscribedAt = useRef(+new Date());
    // "socket.io-react-hook" doesn't allow to subscribe on the next events but sometimes we like to
    const isLibSystemEvent = useMemo(() => [SocketEvent.Connect, SocketEvent.Disconnect].includes(event as SocketEvent), [event]);
    const isManagerEvent = useMemo(() => values(SocketManagerEvent).includes(event as SocketManagerEvent), [event]);
    const canProceedWithStandardFlow = useMemo(() => !isLibSystemEvent && !isManagerEvent, [isLibSystemEvent, isManagerEvent]);
    const extendedCallback = useMemo(() => extendCallback(callback, subscribedAt.current, {new: opts?.new}), [callback, opts?.new]);

    const onAuthDataStoreUpdated = useCallback((data: AuthDataStore, prevData: AuthDataStore) => {
        if (data.logoutInProgress && prevData && !prevData.logoutInProgress) {
            // clean up local data on logout
            resetOnLogout.current = true;
            setLastMessage(socket.namespaceKey, event, undefined);
            setLastMessageQueue([]);
        }
    }, [event, socket?.namespaceKey, setLastMessage]);

    useEffect(() => {
        // a fix for reset lastMessage on hot reload while development
        // should be a first useEffect in this file
        if (initialized.current) {
            initialized.current = false;
        }
    }, [event]);

    useEffect(() => {
        if (!canProceedWithStandardFlow && !subscribedOnLibSystemEvent.current && socket.namespaceKey) {
            if (isManagerEvent) {
                socket.io.on<any>(event as SocketManagerEvent, extendedCallback);
            } else {
                socket.on(event, extendedCallback);
            }
            subscribedOnLibSystemEvent.current = true;
            return () => {
                subscribedOnLibSystemEvent.current = false;
                if (isManagerEvent) {
                    socket.io.off<any>(event as SocketManagerEvent, extendedCallback);
                } else {
                    socket.off(event, extendedCallback);
                }
            };
        }
    }, [canProceedWithStandardFlow, isManagerEvent, event, socket, extendedCallback]);

    useEffect(() => {
        if (canProceedWithStandardFlow) {
            if (lastMessageQueue.length && !resetOnLogout.current) {
                const firstMessage = lastMessageQueue[0];
                setLastMessageQueue((prev) => drop(prev));
                extendedCallback(firstMessage);
            }
        }
    }, [canProceedWithStandardFlow, lastMessage, lastMessageQueue, extendedCallback]);

    useEffect(() => {
        if (canProceedWithStandardFlow) {
            if (initialized.current || opts?.emitOnInit) {
                setLastMessageQueue((prev) => [...prev, lastMessage]);
            }
            if (!initialized.current) {
                initialized.current = true;
            }
        }
    }, [canProceedWithStandardFlow, lastMessage, opts?.emitOnInit]);

    useEffect(() => authDataStore.subscribe(onAuthDataStoreUpdated), [onAuthDataStoreUpdated]);
};
